import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { runStoreHullsV1RunsRunIdStoreHullsGet } from "../api/generated/qcsControlPlane";
import type { StoreHull } from "../api/generated/model";
import { fmtInt } from "../api/labels";
import { Badge, Button, Panel } from "@akaike/ui";

// A single map that OVERLAYS each store's run-A (day) footprint beneath its run-B
// (night) footprint, so the change between the two runs is visible in place: the
// dashed grey ring is where the store served in A, the solid coloured fill is B, and
// the B fill spilling outside the dashed ring is the extension (or, when B is smaller,
// the dashed ring outside the fill is the area it gave up). A role toggle switches
// which footprint is drawn (all / primary / secondary), using the per-role hulls the
// server now returns, the primary-vs-secondary axis the side-by-side diff can't show.

const APPEARED = "#2ea043";
const DISAPPEARED = "#f85149";
const GREW = "#1f6feb";
const SHRANK = "#d29922";
const SAME = "#8b949e";
const DAY = "#8b949e"; // run-A / day baseline outline
const INDIA: L.LatLngTuple = [22.6, 80.0];

// Material footprint-change thresholds (mirrors RunChangesTable).
const MIN_CELL_DELTA = 3;
const MIN_PCT = 25;

type Role = "all" | "primary" | "secondary";
type Change = "appeared" | "disappeared" | "grew" | "shrank" | "same";
type Filter = "all" | Change;

const CHANGE_COLOR: Record<Change, string> = {
  appeared: APPEARED,
  disappeared: DISAPPEARED,
  grew: GREW,
  shrank: SHRANK,
  same: SAME,
};
const CHANGE_LABEL: Record<Change, string> = {
  appeared: "new in B",
  disappeared: "gone in B",
  grew: "grew",
  shrank: "shrank",
  same: "unchanged",
};

function roleBoundary(s: StoreHull | undefined, role: Role): [number, number][] {
  if (!s) return [];
  if (role === "primary") return (s.primary_boundary ?? []) as [number, number][];
  if (role === "secondary") return (s.secondary_boundary ?? []) as [number, number][];
  return (s.boundary ?? []) as [number, number][];
}
function roleCells(s: StoreHull | undefined, role: Role): number {
  if (!s) return 0;
  if (role === "primary") return s.primary_cells ?? 0;
  if (role === "secondary") return s.secondary_cells ?? 0;
  return s.cells ?? 0;
}

type Entry = {
  id: string;
  name: string;
  change: Change;
  a?: StoreHull;
  b?: StoreHull;
  lat: number;
  lon: number;
  magnitude: number; // |cell delta|, for sorting
};

export function StoreOverlayMap({
  runIdA,
  runIdB,
  labelA,
  labelB,
}: {
  runIdA: string;
  runIdB: string;
  labelA: string;
  labelB: string;
}) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const fittedRef = useRef(false);
  const [role, setRole] = useState<Role>("all");
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<string>("");

  // Cached by the same keys the side-by-side maps use, no extra fetch.
  const hullsA = useQuery({
    queryKey: ["store-hulls", runIdA],
    queryFn: () => runStoreHullsV1RunsRunIdStoreHullsGet(runIdA),
  });
  const hullsB = useQuery({
    queryKey: ["store-hulls", runIdB],
    queryFn: () => runStoreHullsV1RunsRunIdStoreHullsGet(runIdB),
  });

  // One entry per store present in either run, classified by total-cell change.
  const entries = useMemo<Entry[]>(() => {
    const aById = new Map((hullsA.data?.stores ?? []).map((s) => [s.store_id, s]));
    const bById = new Map((hullsB.data?.stores ?? []).map((s) => [s.store_id, s]));
    const ids = new Set([...aById.keys(), ...bById.keys()]);
    const out: Entry[] = [];
    for (const id of ids) {
      const a = aById.get(id);
      const b = bById.get(id);
      let change: Change;
      if (!a) change = "appeared";
      else if (!b) change = "disappeared";
      else {
        const delta = b.cells - a.cells;
        const pct = a.cells > 0 ? (delta / a.cells) * 100 : 0;
        change =
          Math.abs(delta) >= MIN_CELL_DELTA && Math.abs(pct) >= MIN_PCT
            ? delta > 0
              ? "grew"
              : "shrank"
            : "same";
      }
      const anchor = b ?? a!;
      out.push({
        id,
        name: (b ?? a)?.name ?? "",
        change,
        a,
        b,
        lat: anchor.lat,
        lon: anchor.lon,
        magnitude: Math.abs((b?.cells ?? 0) - (a?.cells ?? 0)),
      });
    }
    out.sort((x, y) => y.magnitude - x.magnitude);
    return out;
  }, [hullsA.data, hullsB.data]);

  const counts = useMemo(() => {
    const c: Record<Change, number> = {
      appeared: 0,
      disappeared: 0,
      grew: 0,
      shrank: 0,
      same: 0,
    };
    for (const e of entries) c[e.change]++;
    return c;
  }, [entries]);

  const shown = useMemo(() => {
    let list = filter === "all" ? entries : entries.filter((e) => e.change === filter);
    if (selected) list = list.filter((e) => e.id === selected);
    return list;
  }, [entries, filter, selected]);

  const selectedEntry = useMemo(
    () => entries.find((e) => e.id === selected),
    [entries, selected],
  );

  // -- create the map once -------------------------------------------------------
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      attributionControl: false,
      preferCanvas: true,
      worldCopyJump: true,
    }).setView(INDIA, 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
      fittedRef.current = false;
    };
  }, []);

  // -- fit once to the union of both runs' footprints ----------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || fittedRef.current || entries.length === 0) return;
    const pts: L.LatLngTuple[] = entries.map((e) => [e.lat, e.lon]);
    fittedRef.current = true;
    map.fitBounds(L.latLngBounds(pts).pad(0.1), { maxZoom: 13 });
  }, [entries]);

  // -- redraw on data / role / filter / selection change -------------------------
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const e of shown) {
      const color = CHANGE_COLOR[e.change];
      const aRing = roleBoundary(e.a, role);
      const bRing = roleBoundary(e.b, role);
      // Run-A (day) footprint: dashed grey baseline, no fill.
      if (aRing.length >= 3) {
        L.polygon(aRing, {
          color: DAY,
          weight: 1.2,
          dashArray: "4 4",
          fill: false,
        })
          .on("click", () => setSelected(e.id))
          .addTo(layer);
      }
      // Run-B (night) footprint: solid, filled: area outside the dashed ring = extension.
      if (bRing.length >= 3) {
        L.polygon(bRing, {
          color,
          weight: 1.6,
          fill: true,
          fillColor: color,
          fillOpacity: 0.22,
        })
          .on("click", () => setSelected(e.id))
          .bindPopup(popupHtml(e, role))
          .addTo(layer);
      }
      // Store marker (anchor), so appeared/disappeared with no role hull are still visible.
      L.circleMarker([e.lat, e.lon], {
        radius: 4,
        color,
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.9,
      })
        .on("click", () => setSelected(e.id))
        .bindPopup(popupHtml(e, role))
        .addTo(layer);
    }
  }, [shown, role]);

  // -- fly to the selected store -------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEntry) return;
    map.setView([selectedEntry.lat, selectedEntry.lon], Math.max(map.getZoom(), 13), {
      animate: true,
    });
  }, [selectedEntry]);

  const loading = hullsA.isLoading || hullsB.isLoading;
  const error = hullsA.isError || hullsB.isError;
  const noRoleData =
    role !== "all" &&
    !loading &&
    entries.every((e) => roleCells(e.a, role) === 0 && roleCells(e.b, role) === 0);

  return (
    <Panel style={{ gridColumn: "span 12", marginTop: 14 }}>
      <Panel.Head style={{ flexWrap: "wrap", gap: 8 }} title="Store footprints · A over B overlay · extension region">
        <div className="flex-1" />
        <RoleToggle role={role} setRole={setRole} />
      </Panel.Head>
      <Panel.Body tight>
        <div
          className="row"
          style={{
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
            padding: "2px 4px 10px",
          }}
        >
          <span className="faint" style={{ fontSize: 11 }}>
            dashed = {labelA} (day) · solid fill = {labelB} (night). Fill outside the dashed ring is
            the extension.
          </span>
          <div className="flex-1" />
          <select
            className="select mono"
            style={{
              width: "auto",
              minWidth: 220,
              padding: "5px 8px",
              fontSize: 11,
            }}
            value={selected}
            onChange={(e) => setSelected(e.currentTarget.value)}
          >
            <option value="">all stores</option>
            {entries.map((e) => (
              <option key={e.id} value={e.id}>
                {e.id} · {CHANGE_LABEL[e.change]}
                {e.name ? ` · ${e.name}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="row" style={{ gap: 6, flexWrap: "wrap", padding: "0 4px 10px" }}>
          {chip(filter, setFilter, "all", `all (${entries.length})`)}
          {chip(filter, setFilter, "grew", `▲ ${counts.grew} grew`, GREW)}
          {chip(filter, setFilter, "shrank", `▼ ${counts.shrank} shrank`, SHRANK)}
          {chip(filter, setFilter, "appeared", `+${counts.appeared} new`, APPEARED)}
          {chip(filter, setFilter, "disappeared", `−${counts.disappeared} gone`, DISAPPEARED)}
          {chip(filter, setFilter, "same", `${counts.same} same`, SAME)}
        </div>

        {selectedEntry && <SelectedBar e={selectedEntry} onClear={() => setSelected("")} />}
        {noRoleData && (
          <div className="muted" style={{ fontSize: 12, padding: "0 4px 8px" }}>
            No {role} cells recorded in either run, this platform may not distinguish routing roles.
          </div>
        )}

        <div style={{ position: "relative" }}>
          <div
            ref={elRef}
            style={{
              width: "100%",
              height: 520,
              borderRadius: 6,
              overflow: "hidden",
              zIndex: 0,
            }}
          />
          {error ? (
            <Overlay>Could not load store hulls (is ClickHouse configured?).</Overlay>
          ) : !loading && entries.length === 0 ? (
            <Overlay>No store footprints recorded for these runs.</Overlay>
          ) : null}
        </div>
      </Panel.Body>
    </Panel>
  );
}

function popupHtml(e: Entry, role: Role): string {
  const name = e.name ? `<br/>${e.name}` : "";
  const a = roleCells(e.a, role);
  const b = roleCells(e.b, role);
  const roleLbl = role === "all" ? "cells" : `${role} cells`;
  return `<b>${e.id}</b> · ${CHANGE_LABEL[e.change]}${name}<br/>${roleLbl}: ${a} → ${b}`;
}

function RoleToggle({ role, setRole }: { role: Role; setRole: (r: Role) => void }) {
  const btn = (r: Role, label: string) => (
    <Button size="sm" variant={role === r ? "default" : "outline"} style={{ fontSize: 11, padding: "4px 9px" }} onClick={() => setRole(r)}>
      {label}
    </Button>
  );
  return (
    <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
      {btn("all", "all cells")}
      {btn("primary", "primary")}
      {btn("secondary", "secondary")}
    </div>
  );
}

function SelectedBar({ e, onClear }: { e: Entry; onClear: () => void }) {
  const color = CHANGE_COLOR[e.change];
  const row = (label: string, a: number, b: number) => {
    const d = b - a;
    const pct = a > 0 ? Math.round((d / a) * 100) : null;
    return (
      <span className="num" style={{ fontSize: 11.5 }}>
        <span className="faint">{label}</span> {fmtInt(a)} → {fmtInt(b)}{" "}
        <span
          style={{
            color: d === 0 ? "var(--muted)" : d > 0 ? "var(--ok)" : "var(--crit)",
          }}
        >
          ({d > 0 ? "+" : ""}
          {fmtInt(d)}
          {pct != null ? `, ${pct > 0 ? "+" : ""}${pct}%` : ""})
        </span>
      </span>
    );
  };
  return (
    <div
      className="col"
      style={{
        gap: 6,
        padding: "8px 10px",
        margin: "0 4px 10px",
        border: `1px solid ${color}`,
        borderRadius: 6,
        background: "var(--panel-2, rgba(255,255,255,0.02))",
      }}
    >
      <div className="row" style={{ gap: 12, alignItems: "center" }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
          {e.id}
        </span>
        <Badge variant="outline" className="bg-card" style={{ color, borderColor: color, fontSize: 10.5 }}>
          {CHANGE_LABEL[e.change]}
        </Badge>
        {e.name && <span className="faint" style={{ fontSize: 12 }}>{e.name}</span>}
        <div className="flex-1" />
        <Button variant="outline" size="sm" style={{ fontSize: 11 }} onClick={onClear}>
          clear
        </Button>
      </div>
      <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
        {row("total", e.a?.cells ?? 0, e.b?.cells ?? 0)}
        {row("primary", e.a?.primary_cells ?? 0, e.b?.primary_cells ?? 0)}
        {row("secondary", e.a?.secondary_cells ?? 0, e.b?.secondary_cells ?? 0)}
      </div>
    </div>
  );
}

function chip(
  filter: Filter,
  setFilter: (f: Filter) => void,
  f: Filter,
  label: string,
  color?: string,
) {
  return (
    <Button
      size="sm"
      variant={filter === f ? "default" : "outline"}
      style={{
        fontSize: 11,
        padding: "4px 9px",
        borderColor: color,
        color: filter === f ? undefined : color,
      }}
      onClick={() => setFilter(f)}
    >
      {label}
    </Button>
  );
}

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="muted"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12.5,
        pointerEvents: "none",
        background: "rgba(0,0,0,0.04)",
        zIndex: 400,
      }}
    >
      {children}
    </div>
  );
}
